import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Alert,
  Unstable_Grid2 as Grid,
  Stack,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useMutation } from '@apollo/client';
import { AUTH_QUERY, UPDATE_ACCOUNT_MUTATION } from '../../gql';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, FieldLabel, SurfaceTextField } from '../design-system';

interface UserSettingsFormProps {
  user: {
    name: string;
    role: string;
    updatedAt: string;
    createdAt: string;
    displayName?: string;
    firstName?: string;
    lastName?: string;
    email: string;
  };
}

const containerStyles = { maxWidth: 640, flexGrow: 1 };
const inputStyles = { width: '100%' };
const labelStyles = { fontWeight: 600 };
const submitButtonStyles = {
  borderRadius: 2,
  fontSize: '1rem',
};
const successAlertStyles = { width: '100px' };

const UserSettingsForm = React.memo(({ user }: UserSettingsFormProps) => {
  const intl = useIntl();
  const [editPassword, setEditPassword] = useState(false);
  const [
    updateAccount,
    { called: calledUpdate, loading: loadingUpdate },
  ] = useMutation(UPDATE_ACCOUNT_MUTATION, {
    refetchQueries: [{ query: AUTH_QUERY }],
  });

  const handleEditPassword = () => {
    setEditPassword(true);
  };

  const defaultValues = user
    ? {
        displayName: user.displayName,
        firstName: user.firstName,
        lastName: user.lastName,
        oldPassword: undefined,
        newPassword: undefined,
        newPasswordConfirm: undefined,
      }
    : {};

  const {
    reset,
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm({
    defaultValues,
  });

  const onSubmit = async (data) => {
    await updateAccount({
      variables: {
        user: {
          displayName: data.displayName,
          firstName: data.firstName,
          lastName: data.lastName,
          newPassword: data.newPassword,
          oldPassword: data.oldPassword,
        },
      },
    });

    // add yup validation and error handling and displaying

    reset(data);
  };

  return (
    <Stack
      onSubmit={handleSubmit(onSubmit)}
      component="form"
      autoComplete="off"
      gap={2}
      sx={containerStyles}
    >
      <Grid container spacing={2} alignItems="center">
        <Grid
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          xs={3}
          md={3}
        >
          <Typography sx={labelStyles}>
            <FormattedMessage id="account.email" defaultMessage="Email" />
          </Typography>
        </Grid>
        <Grid justifyContent="flex-start" alignItems="center" xs={9} md={9}>
          {user.email}
        </Grid>
      </Grid>
      <Grid container spacing={2} alignItems="center">
        <Grid
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          xs={3}
          md={3}
        >
          <Typography sx={labelStyles}>
            <FormattedMessage id="account.role" defaultMessage="Role" />
          </Typography>
        </Grid>
        <Grid justifyContent="flex-start" alignItems="center" xs={9} md={9}>
          {user.role === 'admin'
            ? intl.formatMessage({
                id: 'account.role.admin',
                defaultMessage: 'Admin',
              })
            : user.role === 'user'
            ? intl.formatMessage({
                id: 'account.role.user',
                defaultMessage: 'User',
              })
            : user.role}
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          xs={3}
          md={3}
        >
          <FieldLabel sx={labelStyles} htmlFor="display-name">
            <FormattedMessage
              id="account.displayName"
              defaultMessage="Display name"
            />
          </FieldLabel>
        </Grid>
        <Grid justifyContent="flex-start" alignItems="center" xs={9} md={9}>
          <Controller
            name="displayName"
            control={control}
            render={({ field }) => (
              <SurfaceTextField
                autoComplete="off"
                id="display-name"
                surface="sunken"
                type="text"
                sx={inputStyles}
                {...field}
              />
            )}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          xs={3}
          md={3}
        >
          <FieldLabel sx={labelStyles} htmlFor="first-name">
            <FormattedMessage
              id="account.firstNamePrivate"
              defaultMessage="First name (private)"
            />
          </FieldLabel>
        </Grid>
        <Grid justifyContent="flex-start" alignItems="center" xs={9} md={9}>
          <Controller
            name="firstName"
            control={control}
            render={({ field }) => (
              <SurfaceTextField
                autoComplete="off"
                id="first-name"
                surface="sunken"
                type="text"
                sx={inputStyles}
                {...field}
              />
            )}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          xs={3}
          md={3}
        >
          <FieldLabel sx={labelStyles} htmlFor="last-name">
            <FormattedMessage
              id="account.lastNamePrivate"
              defaultMessage="Last name (private)"
            />
          </FieldLabel>
        </Grid>
        <Grid justifyContent="flex-start" alignItems="center" xs={9} md={9}>
          <Controller
            name="lastName"
            control={control}
            render={({ field }) => (
              <SurfaceTextField
                autoComplete="off"
                id="last-name"
                surface="sunken"
                type="text"
                sx={inputStyles}
                {...field}
              />
            )}
          />
        </Grid>
      </Grid>
      {!editPassword && (
        <Grid container spacing={2}>
          <Grid
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            xs={3}
            md={3}
          >
            <FieldLabel sx={labelStyles} htmlFor="new-password">
              <FormattedMessage
                id="account.password"
                defaultMessage="Password"
              />
            </FieldLabel>
          </Grid>
          <Grid justifyContent="flex-start" alignItems="center" xs={9} md={9}>
            <Button
              type="button"
              onClick={handleEditPassword}
              tone="neutral"
              endIcon={<EditIcon fontSize="small" />}
            >
              <FormattedMessage
                id="account.password.edit"
                defaultMessage="Edit password"
              />
            </Button>
          </Grid>
        </Grid>
      )}
      {editPassword && (
        <Grid container spacing={2}>
          <Grid
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            xs={3}
            md={3}
          >
            <FieldLabel sx={labelStyles} htmlFor="old-password">
              <FormattedMessage
                id="account.password.old"
                defaultMessage="Old password"
              />
            </FieldLabel>
          </Grid>
          <Grid justifyContent="flex-start" alignItems="center" xs={9} md={9}>
            <Controller
              name="oldPassword"
              control={control}
              render={({ field }) => (
                <SurfaceTextField
                  autoComplete="off"
                  id="old-password"
                  surface="sunken"
                  type="password"
                  sx={inputStyles}
                  {...field}
                />
              )}
            />
          </Grid>
        </Grid>
      )}
      {editPassword && (
        <Grid container spacing={2}>
          <Grid
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            xs={3}
            md={3}
          >
            <FieldLabel sx={labelStyles} htmlFor="new-password">
              <FormattedMessage
                id="account.password.new"
                defaultMessage="New password"
              />
            </FieldLabel>
          </Grid>
          <Grid justifyContent="flex-start" alignItems="center" xs={9} md={9}>
            <Controller
              name="newPassword"
              control={control}
              render={({ field }) => (
                <SurfaceTextField
                  autoComplete="off"
                  id="new-password"
                  surface="sunken"
                  type="password"
                  sx={inputStyles}
                  {...field}
                />
              )}
            />
          </Grid>
        </Grid>
      )}
      {editPassword && (
        <Grid container spacing={2}>
          <Grid
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            xs={3}
            md={3}
          >
            <FieldLabel sx={labelStyles} htmlFor="confirm-new-password">
              <FormattedMessage
                id="account.password.confirm"
                defaultMessage="Confirm new password"
              />
            </FieldLabel>
          </Grid>
          <Grid justifyContent="flex-start" alignItems="center" xs={9} md={9}>
            <Controller
              name="newPasswordConfirm"
              control={control}
              render={({ field }) => (
                <SurfaceTextField
                  id="confirm-new-password"
                  surface="sunken"
                  type="password"
                  sx={inputStyles}
                  {...field}
                />
              )}
            />
          </Grid>
        </Grid>
      )}
      <Grid container spacing={2}>
        <Grid
          mdOffset={3}
          justifyContent="flex-start"
          alignItems="center"
          xs={8}
        >
          <Stack direction="row" gap={1}>
            <Button
              disabled={loadingUpdate || !isDirty}
              type="submit"
              sx={submitButtonStyles}
            >
              <FormattedMessage id="general.save" defaultMessage="Save" />
            </Button>
            {calledUpdate && !isDirty && (
              <Alert sx={successAlertStyles} severity="success">
                <FormattedMessage id="general.saved" defaultMessage="Saved!" />
              </Alert>
            )}
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
});

export { UserSettingsForm };
